import React, { memo, useCallback } from "react"
import PropTypes from "prop-types"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Button, ButtonGroup } from "reactstrap"
import { oldRoutes } from "router/old-routes"
import { routes } from "router/routes"
import PhotoSlider from "../common/PhotoSlider"
import { Flex, message } from "antd"
import Icon from "components/common/Icon"
import { Icon as FeatherIcon } from "ui/Icon"
import CircleImage from "components/common/CircleImage"
import { useConfirmModal } from "modules/modals/hooks/useConfirmModal"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { calculateChanges } from "components/bookings/helpers"
import pluralize from "pluralize"
import { useDispatch, useSelector } from "react-redux"
import { duplicateTrip, updateTrip } from "store/trips"
import { showModal } from "modules/modals/reducer"
import { createFavoriteTrip, deleteFavouriteTrip } from "store/favourite-trips"
import { isGuideSelector } from "store/user"
import { generateScriptForEmbedCheckout } from "./utils"
import { getHostsByEnvironment } from "utils/host-urls"
import { resetBookingSelections } from "store/experiences"
import { formatExperienceTerms } from "utils/experience"
import styles from "./Trip.module.css"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { shouldNavigateFromCardClick } from "helpers/card-navigation"
import { FORWARDED_SEARCH_PARAMS } from "./constants"

const Trip = ({
  trip,
  className,
  style,
  onClose,
  preview = false,
  withModal = false,
  withRating = false,
  guideView = false,
  isVertical = false,
  ...rest
}) => {
  const {
    id,
    uuid,
    title,
    description,
    status,
    min_guests,
    max_guests,
    address,
    photos,
    cropped_photos,
    favorite_id,
    min_price,
    trip_prices,
    guide
  } = trip
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const confirmModal = useConfirmModal()
  const loggedIn = useSelector((store) => store.auth.loggedIn)
  const userId = useSelector((state) => state.user.id)
  const isGuide = useSelector(isGuideSelector)
  const calculation = calculateChanges(null, { trip })

  const publishTripHandler = (event) => {
    if (event?.persist) event?.persist?.()
    const id = event?.currentTarget?.value
    if (id) dispatch(updateTrip(id, { trip: { status: "published" } }))
  }
  const bookTripHandler = () => {
    dispatch(resetBookingSelections())
    if (loggedIn) navigate(routes.clientBookingWizardRootPath({ id: uuid }))
    else navigate({ pathname: oldRoutes.bookingWizardPath({ id: uuid }) })
  }

  const toggleFavoriteTrip = useCallback(() => {
    if (withModal && favorite_id) return removeFavoriteTrip(favorite_id)
    if (favorite_id) return dispatch(deleteFavouriteTrip(favorite_id))
    dispatch(createFavoriteTrip(id))
  }, [id, favorite_id, withModal, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const shareHandler = () =>
    dispatch(
      showModal("shareModal", {
        name: "Trip",
        path: routes.tripPath({ id: uuid }),
        description,
        title
      })
    )

  const duplicateHandler = confirmModal({ title: "Are you sure you want to duplicate this trip?", submitText: t("global.yes") }, () =>
    dispatch(duplicateTrip(id))
  )

  const removeFavoriteTrip = confirmModal({ title: "Are you sure?", color: "danger", submitText: t("global.yes") }, () =>
    dispatch(deleteFavouriteTrip(favorite_id))
  )
  const unpublishTripHandler = confirmModal(
    { title: "Are you sure you want to unpublish this trip?", color: "danger", submitText: t("global.yes") },
    ({ currentTarget }) => dispatch(updateTrip(+currentTarget.value, { trip: { status: "unpublished" } }))
  )

  const handleCopyEmbedCode = async (tripId) => {
    const hosts = getHostsByEnvironment()
    try {
      const script = generateScriptForEmbedCheckout(
        `${hosts.PROTOCOL}${hosts.CUSTOMER}/#${oldRoutes.bookingWizardPath()}?trip_id=${tripId}&mode=embed`
      )
      await navigator.clipboard.writeText(script)
      message.success("Embed code copied!")
    } catch (err) {
      message.error("Failed to copy embed code")
    }
  }

  const archiveTripHandler = confirmModal(
    {
      title: "Are you sure you want to archive this trip? ",
      hasSubText: true,
      subText:
        "Note: Archiving this trip will not cancel any scheduled trips with clients. If you wish to cancel those trips, it will need to be done manually.",
      color: "danger",
      submitText: t("global.yes")
    },
    ({ currentTarget }) => dispatch(updateTrip(+currentTarget.value, { trip: { status: "archived" } }))
  )

  const unarchiveTripHandler = confirmModal(
    { title: "Are you sure you want to unarchive this trip?", color: "danger", submitText: t("global.yes") },
    ({ currentTarget }) => dispatch(updateTrip(+currentTarget.value, { trip: { status: "unpublished" } }))
  )

  const classes = ["bg-white rounded shadow-lg position-relative w-100 cursor-pointer flex"]
  if (className) classes.push(className)

  const handleCardNavigate = (event) => {
    if (!shouldNavigateFromCardClick(event)) return
    navigate(routes.tripPath({ id: uuid }))
  }

  // The guide profile keeps the client's current date/party-size selection.
  const guideProfilePath = guide?.slug
    ? routes.proGuideProfilePath(
        { slug: guide.slug },
        FORWARDED_SEARCH_PARAMS.reduce((params, key) => ({ ...params, [key]: searchParams.get(key) || "" }), {})
      )
    : null

  return (
    <button
      className={`${classes.join(" ")} ${styles.tripCard} ${
        isVertical === true ? styles.HorizontalCard : styles.VerticalCard
      } ${preview && isVertical ? styles.mapPreview : ""}`}
      style={{ ...style, cursor: "pointer" }}
      {...rest}
      type="text"
      tabIndex={0}
      onClick={handleCardNavigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleCardNavigate(e)
        }
      }}
      aria-label={`View details for ${title || "Draft Trip"}`}
      aria-describedby={`trip-${id}-description`}
    >
      <div className={`position-relative ${styles.photoContainer}`}>
        <div className="position-absolute top-0 start-0 w-100 p-3 z-2">
          {isGuide ? (
            <StatusForGuide status={status} />
          ) : (
            <UserActions {...{ userId, favorite_id, onClose, toggleFavoriteTrip, onShare: shareHandler }} />
          )}
        </div>
        {!isGuide && <TopRatedBadge />}
        <PhotoSlider photos={photos} cropped_photos={cropped_photos} preview className="w-100 h-100" />
      </div>
      <div
        className={[
          styles.cardContent,
          !isGuide && withRating ? "d-flex flex-column flex-grow-1" : "",
          preview ? "gap-1 p-3" : "gap-3 p-4"
        ].join(" ")}
      >
        <Flex justify="space-between" gap={16} className="w-100 flex-fill flex-wrap">
          <div className={`d-flex flex-column ${preview ? "gap-1" : "gap-3"} ${styles.tripInfo}`}>
            <Link to={routes.tripPath({ id: uuid })} className="link-dark link-underline-opacity-0 text-primary-second-hover">
              <h3 className={["lh-sm", preview ? "h5" : "h4"].join(" ")}>{title || "Draft Trip"}</h3>
            </Link>
            <div>
              <Flex className={styles.address} title={address}>
                <Icon iconName="Location" className="flex-shrink-0" width={20} />
                <span className={`fs-7 ${styles["addressTruncate"]}`}>{address || "—"}</span>
              </Flex>
              <Flex gap={24} align="start" className="w-full">
                <Flex className="hstack gap-2 align-items-start">
                  <Icon iconName="Clock" className="flex-shrink-0" width={20} />
                  <span className="fs-7 mt-1">{formatExperienceTerms(trip, t)}</span>
                </Flex>
                <Flex className={`hstack gap-2 align-items-start ${styles.guestCount}`}>
                  <Icon iconName="Users" className="flex-shrink-0" width={20} />
                  <span className="fs-7 mt-1">
                    {(min_guests === max_guests
                      ? t("trip.card.persons", { count: min_guests })
                      : t("trip.card.persons_range", { min_guests, max_guests })) || "—"}
                  </span>
                </Flex>
              </Flex>

              {isGuide && <Pricing calculation={calculation} />}
            </div>
          </div>
          {!isGuide && withRating && guide && (
            <div className="align-self-end flex-shrink-0">
              <GuideRating name={guide.company_name || guide.display_name} logo={guide.logo} profilePath={guideProfilePath} />
            </div>
          )}
        </Flex>
        {isGuide ? (
          <GuideButtons
            {...{
              id,
              status,
              publishTripHandler,
              unpublishTripHandler,
              duplicateHandler,
              archiveTripHandler,
              unarchiveTripHandler,
              handleCopyEmbedCode
            }}
          />
        ) : (
          <UserButtons {...{ calculation, bookTripHandler, min_price, trip_prices }} />
        )}
      </div>
    </button>
  )
}

const StatusForGuide = ({ status }) => {
  const { t } = useTranslation()
  return (
    <div className="hstack fs-6 fw-semibold text-white">
      {(status === "published" && (
        <div className="hstack bg-primary rounded-pill p-1 pe-2">
          <Icon iconName="Checked" className="me-1" />
          {t("trip.card.published")}
        </div>
      )) ||
        (status === "archived" && (
          <div className="hstack bg-primary-second rounded-pill p-1 pe-2">
            <Icon iconName="LegalProfessional" className="me-1" />
            {t("trip.card.archived")}
          </div>
        )) || <div className="hstack bg-gray-light rounded-pill py-1 px-25">{t("trip.card.draft")}</div>}
    </div>
  )
}

StatusForGuide.propTypes = {
  status: PropTypes.string
}

// Static "Top Rated" corner badge shown on client-facing cards
const TopRatedBadge = () => (
  <div className={`hstack gap-1 align-items-center text-dark fw-semibold fs-7 ${styles.topRatedBadge}`}>
    <FeatherIcon type="feather" name="Award" color="#212529" width={16} height={16} />
    <span>Top Rated</span>
  </div>
)

const UserActions = memo(({ userId, favorite_id, onClose, toggleFavoriteTrip, onShare: shareHandler }) => (
  <div className="hstack gap-10">
    {typeof onClose === "function" && (
      <Button color="icon" onClick={onClose} className="p-0 border-0 me-auto position-relative">
        <div
          className="bg-dark bg-opacity-50 rounded-circle position-absolute top-50 start-50 translate-middle"
          style={{ width: 18, height: 18 }}
        />
        <Icon iconName="CloseFill" className="text-white text-opacity-75-hover position-relative z-1" size={20} block />
      </Button>
    )}
    <div className="hstack gap-1">
      {userId && (
        <Button
          color="ghost"
          className={[
            "p-1",
            favorite_id ? "bg-primary bg-opacity-75-hover text-white" : "bg-white bg-opacity-25 bg-opacity-100-hover text-dark"
          ].join(" ")}
          onClick={toggleFavoriteTrip}
        >
          <Icon iconName="Like" className={["flex-shrink-0"].join(" ")} size={18} block />
        </Button>
      )}
      <Button color="ghost" className="bg-white bg-opacity-25 bg-opacity-100-hover p-1" onClick={shareHandler}>
        <Icon iconName="Share" className="flex-shrink-0" size={18} block />
      </Button>
    </div>
  </div>
))

UserActions.propTypes = {
  userId: PropTypes.string,
  favorite_id: PropTypes.string,
  onClose: PropTypes.func,
  toggleFavoriteTrip: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired
}

const Pricing = memo(({ calculation, className }) => {
  const { t } = useTranslation()

  let classes = "hstack gap-2 align-items-start"
  if (className) classes = classes.concat(" ", className)

  if (!calculation.pricingType) return "—"

  return (
    <div className={classes}>
      <Icon iconName="Coin" className="flex-shrink-0" width={20} />
      <small
        className="fs-7"
        dangerouslySetInnerHTML={{
          __html: t(`trip.card.starting_price.${calculation.pricingType}`, {
            flatRate: numberToCurrency(calculation.flatRate),
            minimumRate: numberToCurrency(calculation.minimumRate),
            minimumPersons: pluralize("person", calculation.minimumPersons, true),
            pricePerPerson: numberToCurrency(calculation.pricePerPerson)
          })
        }}
      />
    </div>
  )
})

Pricing.propTypes = {
  calculation: PropTypes.shape({
    pricingType: PropTypes.string,
    flatRate: PropTypes.number,
    minimumRate: PropTypes.number,
    minimumPersons: PropTypes.number,
    pricePerPerson: PropTypes.number
  }).isRequired,
  className: PropTypes.string
}

// TODO: star rating is still static until the guide rating is wired up
const GUIDE_RATING_STARS = 5

const GuideRating = ({ name, logo, profilePath }) => {
  const content = (
    <>
      <CircleImage
        src={logo}
        alt={name || "Guide"}
        size={40}
        placeholderIconName="MyProfile"
        className={`flex-shrink-0 ${styles.guideLogo}`}
        border={false}
        isContain
      />
      <div className="d-flex flex-column align-items-start">
        <span className="fs-7 fw-semibold text-dark lh-sm mb-1">{name}</span>
        <div className="hstack gap-1">
          {new Array(GUIDE_RATING_STARS).fill(null).map((_, index) => (
            <Icon key={index} iconName="StarNew" className="text-primary-second" size={16} block />
          ))}
        </div>
      </div>
    </>
  )

  if (!profilePath) return <div className="hstack gap-2 flex-shrink-0">{content}</div>

  return (
    <Link
      to={profilePath}
      className="hstack gap-2 flex-shrink-0 link-dark link-underline-opacity-0 text-primary-second-hover"
      title={name}
    >
      {content}
    </Link>
  )
}

GuideRating.propTypes = {
  name: PropTypes.string,
  logo: PropTypes.string,
  profilePath: PropTypes.string
}

const UserButtons = memo(({ calculation, bookTripHandler, min_price, trip_prices }) => {
  const { t } = useTranslation()
  return (
    <div className="hstack gap-10 flex-column">
      <div className={`justify-content-start hstack flex-fill gap-2 flex-wrap ${styles.section}`}>
        <div className="vstack gap-1 align-items-end me-auto">
          <div className="hstack gap-1 align-items-end me-auto">
            <small>from</small>
            <span className={`fw-medium lh-1 ${styles.priceText}`}>
              {numberToCurrency(min_price)}
            </span>
            <span className="fs-7">
              <small>/booking</small>
            </span>
            <span className="no-card-nav d-inline-flex">
              <PricingDetailsTooltip tripPrices={trip_prices} />
            </span>
          </div>
        </div>
        <Button
          color="primary"
          className="px-4 py-2 fs-7"
          onClick={bookTripHandler}
          onTouchStart={(e) => {
            e.stopPropagation()
            e.preventDefault()
            bookTripHandler()
          }}
        >
          {t("global.book")}
        </Button>
      </div>
    </div>
  )
})

UserButtons.propTypes = {
  calculation: PropTypes.shape({
    pricingType: PropTypes.string,
    flatRate: PropTypes.number,
    minimumRate: PropTypes.number,
    minimumPersons: PropTypes.number,
    pricePerPerson: PropTypes.number,
  }).isRequired,
  bookTripHandler: PropTypes.func.isRequired,
  min_price: PropTypes.number.isRequired,
  trip_prices: PropTypes.array.isRequired,
}

const GuideButtons = memo(
  ({
    id,
    status,
    publishTripHandler,
    unpublishTripHandler,
    duplicateHandler,
    archiveTripHandler,
    unarchiveTripHandler,
    handleCopyEmbedCode
  }) => {
    const { t } = useTranslation()
    return (
      <div className="hstack gap-10 flex-wrap flex-row-reverse mt-auto h-full">
        <Button
          color="link"
          className="link-danger link-underline-opacity-0 link-underline-opacity-75-hover p-0 ms-auto fs-7"
          value={id}
          onClick={status === "archived" ? unarchiveTripHandler : archiveTripHandler}
        >
          <small>{status === "archived" ? t("trip.card.unarchive") : t("global.archive")}</small>
        </Button>
        <div className="hstack gap-10 flex-fil flex-wrap">
          {(status === "unpublished" && (
            <Button color="primary" className="px-5 py-2 fs-7 flex-fill" value={id} onClick={publishTripHandler}>
              {t("global.publish")}
            </Button>
          )) ||
            (status === "published" && (
              <Button color="gray-light" className="px-5 py-2 fs-7 flex-fill text-white" value={id} onClick={unpublishTripHandler}>
                {t("global.unpublish")}
              </Button>
            ))}
          <ButtonGroup>
            <Button
              tag={Link}
              color="primary-second"
              outline
              className="px-5 py-2 fs-7 flex-fill"
              to={
                status === "draft"
                  ? routes.proNewExperienceV2Path({ id })
                  : routes.proEditExperienceV2Path({ id }, { route: ["deactivated", "unpublished"].includes(status) ? "deactivated" : "published" })
              }
            >
              {status === "draft" ? t("global.continue") : t("global.edit")}
            </Button>
            {status !== "draft" && (
              <Button color="primary-second" outline className="px-10 py-2 fs-7 flex-fill" onClick={duplicateHandler}>
                <i className="fas fa-clone" />
              </Button>
            )}
            {status === "published" && (
              <Button color="primary-second" outline className="px-10 py-2 fs-7 flex-fill" onClick={() => handleCopyEmbedCode(id)}>
                <i className="fas fa-code" />
              </Button>
            )}
          </ButtonGroup>
        </div>
      </div>
    )
  }
)

GuideButtons.propTypes = {
  id: PropTypes.string.isRequired,
  status: PropTypes.string,
  publishTripHandler: PropTypes.func.isRequired,
  unpublishTripHandler: PropTypes.func.isRequired,
  duplicateHandler: PropTypes.func.isRequired,
  archiveTripHandler: PropTypes.func.isRequired,
  unarchiveTripHandler: PropTypes.func.isRequired,
  handleCopyEmbedCode: PropTypes.func.isRequired
}

Trip.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    min_guests: PropTypes.number,
    max_guests: PropTypes.number,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    address: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.string),
    cropped_photos: PropTypes.arrayOf(PropTypes.string),
    favorite_id: PropTypes.string,
    can_be_paid: PropTypes.bool,
    uuid: PropTypes.string,
    available_hours_range: PropTypes.string,
    min_price: PropTypes.number,
    trip_prices: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        price: PropTypes.number,
        currency: PropTypes.string,
        start_date: PropTypes.string,
        end_date: PropTypes.string,
        is_active: PropTypes.bool,
        is_default: PropTypes.bool,
        created_at: PropTypes.string,
        updated_at: PropTypes.string
      })
    ),
    guide: PropTypes.shape({
      company_name: PropTypes.string,
      display_name: PropTypes.string,
      logo: PropTypes.string,
      slug: PropTypes.string
    })
  }).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  onClose: PropTypes.func,
  preview: PropTypes.bool,
  withModal: PropTypes.bool,
  withRating: PropTypes.bool,
  guideView: PropTypes.bool,
  isVertical: PropTypes.bool
}

export default memo(Trip)
